import React from "react";
import { Loader2, Wifi, WifiOff } from "lucide-react";
import lightbulbOrange from "../../assets/lightbulb_orange.svg";

interface SpeedTestWarningModalProps {
  isOpen: boolean;
  isTesting: boolean;
  downloadMbps: number;
  uploadMbps: number;
  minDownloadMbps: number;
  minUploadMbps: number;
  onTestAgain: () => void;
  onComeBackLater: () => void;
  currentRetryCount: number;
  maxRetries: number;
  hasReachedMaxRetries: boolean;
  error: string | null;
}

const SpeedTestWarningModal: React.FC<SpeedTestWarningModalProps> = ({
  isOpen,
  isTesting,
  downloadMbps,
  uploadMbps,
  minDownloadMbps,
  minUploadMbps,
  onTestAgain,
  onComeBackLater,
  currentRetryCount,
  maxRetries,
  hasReachedMaxRetries,
  error,
}) => {
  const [autoCloseCountdown, setAutoCloseCountdown] = React.useState<number | null>(null);

  const formatSpeed = (mbps: number) => mbps.toFixed(2);

  const isDownloadLow = downloadMbps < minDownloadMbps;
  const isUploadLow = uploadMbps < minUploadMbps;
  const isPassed = !isDownloadLow && !isUploadLow && downloadMbps > 0 && uploadMbps > 0;

  React.useEffect(() => {
    if (!isOpen) return;

    if (isPassed && !isTesting) {
      setAutoCloseCountdown(3);
      const interval = setInterval(() => {
        setAutoCloseCountdown((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(interval);
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isPassed, isTesting, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
        <div
          className={`flex items-center justify-center px-10 pt-11 pb-8 bg-gradient-to-b ${
            isPassed && !isTesting ? "from-[#E5F9F5] to-[#D5F0E8]" : "from-[#FFE5E5] to-[#FFD5D5]"
          }`}
        >
          <div className="w-24 h-24 flex items-center justify-center rounded-full bg-white/50">
            {isTesting ? (
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
            ) : isPassed ? (
              <Wifi className="w-12 h-12 text-green-500" />
            ) : (
              <WifiOff className="w-12 h-12 text-red-500" />
            )}
          </div>
        </div>

        <div className="space-y-8 p-8">
          <div className="space-y-5">
            <h2 className="text-[#2D2D2D] font-ubuntu text-[24px] font-medium leading-[38.4px]">
              {isTesting ? "Testing Your Connection..." : isPassed ? "Connection Speed Verified ✓" : "Internet Speed Below Threshold"}
            </h2>

            {isTesting && (
              <p className="text-[#2D2D2D] font-ubuntu text-[16px] font-normal leading-[24px]">
                Please wait while we test your internet connection...
                {currentRetryCount > 0 && (
                  <span className="block mt-2 text-[14px] text-[#6F6F6F]">
                    Retry attempt {currentRetryCount} of {maxRetries}
                  </span>
                )}
              </p>
            )}

            {!isTesting && (
              <div className="space-y-3">
                {isPassed ? (
                  <p className="text-green-700 font-ubuntu text-[16px] font-normal leading-[24px]">
                    Your internet connection meets the requirements for a smooth interview experience.
                    {autoCloseCountdown !== null && (
                      <span className="block mt-2 text-[14px] text-[#6F6F6F]">Closing in {autoCloseCountdown}s...</span>
                    )}
                  </p>
                ) : (
                  <>
                    <p className="text-[#2D2D2D] font-ubuntu text-[16px] font-normal leading-[24px]">
                      {error || "Your internet speed is below the recommended threshold for a smooth interview experience."}
                    </p>
                    {currentRetryCount > 0 && (
                      <p className="text-[14px] text-[#6F6F6F]">
                        Attempts: {currentRetryCount} of {maxRetries}
                      </p>
                    )}
                  </>
                )}

                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-[#6F6F6F] text-[14px]">Download Speed:</span>
                    <span className={`font-medium ${isDownloadLow ? "text-red-600" : "text-green-600"}`}>
                      {formatSpeed(downloadMbps)} Mbps
                      <span className="text-[#999] text-[12px] ml-1">(min: {minDownloadMbps} Mbps)</span>
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#6F6F6F] text-[14px]">Upload Speed:</span>
                    <span className={`font-medium ${isUploadLow ? "text-red-600" : "text-green-600"}`}>
                      {formatSpeed(uploadMbps)} Mbps
                      <span className="text-[#999] text-[12px] ml-1">(min: {minUploadMbps} Mbps)</span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {!isTesting && !isPassed && (
            <>
              <div className="flex items-center gap-2 text-[16px] leading-[21px] text-[#00143D] bg-[#FFF6E5] rounded-lg p-2">
                <span>
                  <img src={lightbulbOrange} alt="tip" className="w-5 h-5" />
                </span>
                <span>
                  {hasReachedMaxRetries
                    ? "Maximum retry attempts reached. Please improve your internet connection and try again."
                    : "Try moving closer to your router or disconnecting other devices, then test again."}
                </span>
              </div>

              <div className="flex flex-col gap-3">
                {!hasReachedMaxRetries && (
                  <button className="text-button bg-button text-white px-4 py-2 rounded w-full hover:opacity-90 transition-opacity" onClick={onTestAgain} type="button">
                    Test Again ({maxRetries - currentRetryCount} attempts left)
                  </button>
                )}
                <button
                  className="text-button border border-[#D9DADB] text-[#2D2D2D] px-4 py-2 rounded w-full hover:bg-gray-50 transition-colors"
                  onClick={onComeBackLater}
                  type="button"
                >
                  {hasReachedMaxRetries ? "Exit Interview" : "Exit and Come Back Later"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpeedTestWarningModal;
